import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Menu, Box, UnstyledButton, Group, Text, Tooltip } from "@mantine/core";
import CheckboxFilter from "../../ideas/filters/types/CheckboxFilter";
import { useFiltersContextHelper } from "../../ideas/filters/useFiltersContextHelper";
import { useIdeaFilters } from "../../../hooks/useIdeaFilters";
import {IconChevronDown, IconFolder, IconUsers} from "@tabler/icons";
import { ProductAreaHeaderIcon } from "../../../icons/x-symbol-svgrepo-com";
import { useDiscoveryStore, type DiscoveryStore } from "../../../stores/useDiscoveryStore";
import type { SessionFilter } from "../../../api/discoveryAgent";
import { useActiveDiscoverySession } from "../../../hooks/useDiscoveryAgent";
import {
  PRODUCT_AREA_PARENT,
  enrichSessionFilterSelectedFilterMap,
  buildSelectedFilterMapFromOptions,
  resolveOptionDisplayLabel,
  enrichSessionFiltersObjectIdMaps,
} from "../utils/buildSessionFilters";
import _ from "lodash";
import ChildMenu from "../../ideas/filters/ChildMenu";
import FilterFooter from "../../ideas/filters/FilterFooter";

interface ExploreFilterProps {
  variant?: 'productArea' | 'source' | 'segments';
  filterType: 'productArea' | 'dynamic';
  sessionId?: string | null;
  onFiltersApplied?: (sessionId: string) => void;
  /** Required when filterType is 'dynamic'. A single child filter config object from ideasFiltersConfig. */
  dynamicFilterConfig?: any;
  isNewChat?: boolean;
}

const ExploreFilter: React.FC<ExploreFilterProps> = ({
  variant,
  filterType,
  sessionId,
  onFiltersApplied,
  dynamicFilterConfig,
  isNewChat = false,
}) => {
  const { ideasFiltersConfig } = useIdeaFilters();
  const {
    activeFilters: activeFiltersFromContext,
    setActiveFilters: setContextActiveFilters,
    setSelectedParentFilter,
    setCheckedComponents,
    editMode
  } = useFiltersContextHelper();
  useActiveDiscoverySession();
  const sessionFilters = useDiscoveryStore((s: DiscoveryStore) => s.sessionFilters);
  const updateSessionFilter = useDiscoveryStore((s: DiscoveryStore) => s.updateSessionFilter);
  const setSessionFilters = useDiscoveryStore((s: DiscoveryStore) => s.setSessionFilters);
  const [opened, setOpened] = useState(false);
  const [selectionTooltipOpened, setSelectionTooltipOpened] = useState(false);
  const menuInteractionDisabled = !isNewChat;

  const typeAttributesMap: { [key: string]: { path: string } } = {
    productArea: { path: PRODUCT_AREA_PARENT },
    customers: { path: 'customers' },
  };
  const isDynamicFilter = filterType === 'dynamic';
  const parentKey = isDynamicFilter
    ? (dynamicFilterConfig?.parent ?? '')
    : typeAttributesMap[filterType].path;

  const isEvidenceFilterForConfig = isDynamicFilter
    ? dynamicFilterConfig?.filterObject === 'evidence'
    : false;
  const configRoute = isEvidenceFilterForConfig ? 'evidence' : 'idea';
  const filterNameFromConfig: string | undefined = isDynamicFilter
    ? dynamicFilterConfig?.name
    : _.get(ideasFiltersConfig, `${configRoute}.${parentKey}.name`);

  // For dynamic parent filters (those with childFilters), collect all child parent paths
  // so we can match session filters across all children.
  const childFilterParents = useMemo((): Set<string> => {
    if (!isDynamicFilter || !dynamicFilterConfig?.childFilters) return new Set();
    return new Set(
      Object.values(dynamicFilterConfig.childFilters as Record<string, any>)
        .map((c: any) => c.parent)
        .filter(Boolean) as string[]
    );
  }, [filterType, dynamicFilterConfig]);

  const isDynamicParent = isDynamicFilter && childFilterParents.size > 0;

  // ChildMenu needs both buckets when segments mix evidence + idea children.
  const dynamicInitialFilters = useMemo(() => {
    if (!isDynamicParent) return {};
    const evidence: Record<string, any> = {};
    const idea: Record<string, any> = {};
    Object.entries((dynamicFilterConfig?.childFilters ?? {}) as Record<string, any>).forEach(
      ([key, child]) => {
        if (child?.filterObject === 'idea') idea[key] = child;
        else evidence[key] = child;
      }
    );
    return { evidence, idea };
  }, [isDynamicParent, dynamicFilterConfig]);

  /** Merged segment child rows: ids + optional display labels (e.g. company names for fetchableCheckbox). */
  const mergedSegmentChildByParent = useMemo(() => {
    const m = new Map<
      string,
      { selected: string[]; selectedLabels?: string[]; selectedFilterMap?: Record<string, string> }
    >();
    if (!isDynamicParent) return m;
    sessionFilters.forEach((sf: SessionFilter) => {
      if (sf.parent && childFilterParents.has(sf.parent)) {
        m.set(sf.parent, {
          selected: (sf.selected ?? []).map(String),
          selectedLabels: sf.selectedLabels?.map(String),
          ...(sf.selectedFilterMap && Object.keys(sf.selectedFilterMap).length > 0
            ? { selectedFilterMap: { ...sf.selectedFilterMap } }
            : {}),
        });
      }
    });
    (activeFiltersFromContext ?? []).forEach(
      (f: {
        parent?: string;
        selected?: unknown[];
        selectedLabels?: unknown[];
        selectedFilterMap?: Record<string, string>;
      }) => {
        if (!f.parent || !childFilterParents.has(f.parent)) return;
        const sel = (f.selected ?? []).map(String);
        if (sel.length === 0) {
          m.delete(f.parent);
          return;
        }
        const sl = f.selectedLabels;
        m.set(f.parent, {
          selected: sel,
          selectedLabels:
            Array.isArray(sl) && sl.length === sel.length ? sl.map(String) : undefined,
          ...(f.selectedFilterMap && Object.keys(f.selectedFilterMap).length > 0
            ? { selectedFilterMap: { ...f.selectedFilterMap } }
            : {}),
        });
      }
    );
    return m;
  }, [isDynamicParent, sessionFilters, activeFiltersFromContext, childFilterParents]);

  const value = useMemo(() => {
    const matchByParent = (f: { parent?: string }) =>
      filterType === 'productArea'
        ? f.parent === PRODUCT_AREA_PARENT
        : isDynamicFilter
          ? isDynamicParent
            ? childFilterParents.has(f.parent ?? '')
            : f.parent === parentKey
          : f.parent !== PRODUCT_AREA_PARENT;

    if (!isDynamicParent) {
      return sessionFilters
        .filter(matchByParent)
        .flatMap((f: SessionFilter) => (f.selected ?? []).map(String));
    }

    // "All segments": ChildMenu/CheckboxFilter only update FiltersContext, not Zustand, until we sync
    // on close. Merge per child parent — context wins when present so the button updates on Apply.
    const byParent = new Map<string, string[]>();
    sessionFilters.forEach((sf: SessionFilter) => {
      if (sf.parent && childFilterParents.has(sf.parent)) {
        byParent.set(sf.parent, (sf.selected ?? []).map(String));
      }
    });
    (activeFiltersFromContext ?? []).forEach((f: { parent?: string; selected?: unknown[] }) => {
      if (!f.parent || !childFilterParents.has(f.parent)) return;
      const sel = (f.selected ?? []).map(String);
      if (sel.length === 0) byParent.delete(f.parent);
      else byParent.set(f.parent, sel);
    });
    return Array.from(byParent.values()).flat();
  }, [
    sessionFilters,
    activeFiltersFromContext,
    filterType,
    parentKey,
    isDynamicParent,
    isDynamicFilter,
    childFilterParents,
  ]);

  const setValue = useCallback(
    (next: string[] | ((prev: string[]) => string[])) => {
      const current = useDiscoveryStore.getState().sessionFilters;
      const matchByParent = (f: { parent?: string }) =>
        filterType === 'productArea'
          ? f.parent === PRODUCT_AREA_PARENT
          : isDynamicFilter
            ? isDynamicParent
              ? childFilterParents.has(f.parent ?? '')
              : f.parent === parentKey
            : f.parent !== PRODUCT_AREA_PARENT;
      const prev = current.filter(matchByParent).flatMap((f: SessionFilter) => (f.selected ?? []).map(String));
      const resolved = typeof next === 'function' ? next(prev) : next;
      const route = isEvidenceFilterForConfig ? 'evidence' : 'idea';
      const filterOptions =
        isDynamicFilter && !isDynamicParent
          ? dynamicFilterConfig?.options
          : _.get(ideasFiltersConfig, `${route}.${parentKey}`)?.options;
      updateSessionFilter(parentKey, resolved, undefined, filterNameFromConfig, filterOptions);
    },
    [
      filterType,
      parentKey,
      updateSessionFilter,
      filterNameFromConfig,
      isDynamicParent,
      childFilterParents,
      isEvidenceFilterForConfig,
      ideasFiltersConfig,
      isDynamicFilter,
      dynamicFilterConfig,
    ]
  );

  const setValueForChild = useCallback(
    (value: string[] | null) => setValue(value ?? []),
    [setValue]
  );

  const setOpenedAndNotifyApply = useCallback(
    (open: boolean) => {
      setOpened(open);
      if (!open) {
        const latestSessionFilters = useDiscoveryStore.getState().sessionFilters;
        const fromContext = (activeFiltersFromContext ?? []) as Array<{
          parent?: string;
          type?: string;
          selected?: unknown[];
          selectedLabels?: unknown[];
          selectedFilterMap?: Record<string, string>;
          name?: string;
          filterObject?: 'evidence' | 'idea';
          operator?: string;
          query?: string | number;
        }>;
        const byParent = new Map<string, SessionFilter>(
          latestSessionFilters.map((f: SessionFilter) => [f.parent, f])
        );

        fromContext.forEach((f) => {
          if (f.parent == null) return;
          const selected = (f.selected ?? []).map(String);
          const resolvedName = f.name ?? filterNameFromConfig;
          const fo = f.filterObject;
          const sl = f.selectedLabels;
          const selectedLabelsForEntry =
            Array.isArray(sl) && sl.length === selected.length ? sl.map(String) : undefined;
          const sfm = f.selectedFilterMap;
          const entry = enrichSessionFilterSelectedFilterMap({
            parent: f.parent,
            type: f.type ?? 'checkbox',
            selected,
            filterObject: fo === 'idea' ? 'idea' : 'evidence',
            ...(resolvedName !== undefined && { name: resolvedName }),
            ...(selectedLabelsForEntry !== undefined && { selectedLabels: selectedLabelsForEntry }),
            ...(sfm && Object.keys(sfm).length > 0 && { selectedFilterMap: { ...sfm } }),
            ...(f.operator !== undefined && { operator: f.operator }),
            ...(f.query !== undefined && { query: String(f.query) }),
          });

          // Segment child filters (Plan, Region, …) only exist in context until we persist here.
          if (isDynamicParent && childFilterParents.has(f.parent)) {
            if (selected.length === 0) byParent.delete(f.parent);
            else byParent.set(f.parent, entry);
            return;
          }

          // Product area / source: ids are updated via setValue; Apply puts selectedFilterMap in context only.
          // Merge label metadata from context on close — early return used to drop selectedFilterMap.
          if (byParent.has(f.parent)) {
            const existing = byParent.get(f.parent);
            if (existing === undefined) return;
            const hasLabelData =
              (sfm && Object.keys(sfm).length > 0) ||
              (selectedLabelsForEntry !== undefined && selectedLabelsForEntry.length > 0);
            if (hasLabelData) {
              const sel = (existing.selected ?? []).map(String);
              const mergedSfm = {
                ...(existing.selectedFilterMap ?? {}),
                ...(sfm ?? {}),
              };
              const pruned: Record<string, string> = {};
              for (const id of sel) {
                if (mergedSfm[id]) pruned[id] = mergedSfm[id];
              }
              const labelsForSel =
                selectedLabelsForEntry && selectedLabelsForEntry.length === sel.length
                  ? selectedLabelsForEntry
                  : undefined;
              const withLabels: SessionFilter = {
                ...existing,
                ...(labelsForSel !== undefined && { selectedLabels: labelsForSel }),
              };
              if (Object.keys(pruned).length > 0) {
                withLabels.selectedFilterMap = pruned;
              }
              byParent.set(f.parent, enrichSessionFilterSelectedFilterMap(withLabels));
            }
            return;
          }
          if (selected.length === 0) return;
          byParent.set(f.parent, entry);
        });

        const merged = enrichSessionFiltersObjectIdMaps(
          Array.from(byParent.values()) as SessionFilter[],
          ideasFiltersConfig
        );
        setSessionFilters(merged);
        setContextActiveFilters(merged);
        setCheckedComponents(
          Object.fromEntries(merged.map((f) => [f.parent, (f.selected ?? []).map(String)]))
        );
        if (sessionId && onFiltersApplied) {
          setTimeout(() => onFiltersApplied(sessionId), 0);
        }
      }
    },
    [
      sessionId,
      onFiltersApplied,
      setContextActiveFilters,
      setSessionFilters,
      setCheckedComponents,
      activeFiltersFromContext,
      filterNameFromConfig,
      isDynamicParent,
      childFilterParents,
      ideasFiltersConfig,
    ]
  );

  const typeAttributes: { [key: string]: { path?: string, icon: any, label: string } } = {
    productArea: {
      path: 'product-area',
      icon: ProductAreaHeaderIcon,
      label: 'All product areas'
    },
    source: {
      path: 'source',
      icon: IconFolder,
      label: 'All sources'
    },
    segments: {
      path: 'segments',
      icon: IconUsers,
      label: 'All segments'
    }
  };
  const dynamicVisual = variant ? typeAttributes[variant] : typeAttributes.segments;
  const attributes = isDynamicFilter
    ? { path: parentKey, icon: dynamicVisual.icon, label: dynamicFilterConfig?.label ?? '' }
    : typeAttributes[filterType];
  const IconComponent = attributes.icon;

  /** Selected option id → display label (same resolution as button state / tooltips). */
  const selectedFilterMap = useMemo(() => {
    const out: Record<string, string> = {};
    if (isDynamicParent && dynamicFilterConfig?.childFilters) {
      const childrenByParent = new Map<string, any>(
        Object.values(dynamicFilterConfig.childFilters as Record<string, any>)
          .filter((c: any) => c?.parent)
          .map((c: any) => [c.parent as string, c])
      );
      const order = Object.values(dynamicFilterConfig.childFilters as Record<string, any>)
        .map((c: any) => c.parent as string)
        .filter(Boolean);
      for (const p of order) {
        const row = mergedSegmentChildByParent.get(p);
        if (!row?.selected?.length) continue;
        const child = childrenByParent.get(p);
        const fromChild =
          buildSelectedFilterMapFromOptions(row.selected.map(String), child?.options) ?? {};
        row.selected.forEach((s, i) => {
          const id = String(s);
          out[id] =
            fromChild[id] ??
            row.selectedFilterMap?.[id] ??
            row.selectedLabels?.[i] ??
            id;
        });
      }
      return out;
    }

    const selected: string[] = value || [];
    const route = isEvidenceFilterForConfig ? 'evidence' : 'idea';
    const currentPath = isDynamicFilter
      ? (isDynamicParent ? null : dynamicFilterConfig)
      : _.get(ideasFiltersConfig, `${route}.${attributes.path}`);
    const options: any[] = currentPath?.options || [];
    const fromOptions = buildSelectedFilterMapFromOptions(selected, options) ?? {};
    const storeRow = sessionFilters.find((sf: SessionFilter) => sf.parent === parentKey);
    const fromStore = storeRow?.selectedFilterMap ?? {};
    for (const s of selected) {
      const id = String(s);
      out[id] = fromStore[id] ?? fromOptions[id] ?? id;
    }
    return out;
  }, [
    value,
    ideasFiltersConfig,
    attributes.path,
    isEvidenceFilterForConfig,
    isDynamicParent,
    isDynamicFilter,
    dynamicFilterConfig,
    mergedSegmentChildByParent,
    dynamicFilterConfig,
    sessionFilters,
    parentKey,
  ]);

  const state = useMemo(() => {
    if (isDynamicParent && dynamicFilterConfig?.childFilters) {
      const childrenByParent = new Map<string, any>(
        Object.values(dynamicFilterConfig.childFilters as Record<string, any>)
          .filter((c: any) => c?.parent)
          .map((c: any) => [c.parent as string, c])
      );
      const order = Object.values(dynamicFilterConfig.childFilters as Record<string, any>)
        .map((c: any) => c.parent as string)
        .filter(Boolean);
      let total = 0;
      let first = '';
      const allLabels: string[] = [];
      for (const p of order) {
        const row = mergedSegmentChildByParent.get(p);
        if (!row?.selected?.length) continue;
        const child = childrenByParent.get(p);
        const fromChild =
          buildSelectedFilterMapFromOptions(row.selected.map(String), child?.options) ?? {};
        const resolved = row.selected.map(
          (s, i) =>
            fromChild[String(s)] ??
            row.selectedFilterMap?.[String(s)] ??
            row.selectedLabels?.[i] ??
            s
        );
        total += resolved.length;
        allLabels.push(...resolved);
        if (!first && resolved[0]) first = resolved[0];
      }
      return {
        count: total,
        first,
        selectedLabelsComma: allLabels.join(', '),
      };
    }

    const selected: string[] = value || [];
    const count = selected.length;
    const route = isEvidenceFilterForConfig ? 'evidence' : 'idea';

    const currentPath = isDynamicFilter
      ? (isDynamicParent ? null : dynamicFilterConfig)
      : _.get(ideasFiltersConfig, `${route}.${attributes.path}`);

    const options: any[] = currentPath?.options || [];

    let firstOption: { name?: string; label?: string; picture?: string; _id?: string; value?: string } | null = null;
    const firstSelected = String(selected[0]).toLowerCase();
    firstOption =
        options.find(
          (o: any) =>
            (o.value?.toLowerCase?.() || o.label?.toLowerCase?.()) === firstSelected ||
            (o._id?.toLowerCase?.() || o.domain?.toLowerCase?.()) === firstSelected
        ) || null;

    const labelById = buildSelectedFilterMapFromOptions(selected, options) ?? {};
    const selectedLabelsComma =
      count >= 1
        ? selected.map((s) => labelById[String(s)] ?? String(s)).join(", ")
        : "";

    const id0 = selected.length > 0 ? String(selected[0]) : "";
    let first: string;
    if (selected.length === 0) {
      first = "";
    } else if (filterType === 'productArea') {
      first =
        labelById[id0] ||
        (firstOption ? resolveOptionDisplayLabel(firstOption) : '') ||
        (typeof firstOption?.name === 'string' ? firstOption.name : '') ||
        id0;
    } else if (isDynamicFilter) {
      first =
        labelById[id0] ||
        (firstOption ? resolveOptionDisplayLabel(firstOption) : '') ||
        (typeof firstOption?.label === 'string' ? firstOption.label : '') ||
        (firstOption?.value != null ? String(firstOption.value) : '') ||
        id0;
    } else {
      first =
        labelById[id0] ||
        (firstOption?.value != null ? String(firstOption.value) : '') ||
        id0;
    }

    return { count, first, selectedLabelsComma };
  }, [
    value,
    ideasFiltersConfig,
    attributes.path,
    filterType,
    isEvidenceFilterForConfig,
    isDynamicParent,
    dynamicFilterConfig,
    mergedSegmentChildByParent,
  ]);

  /** Per-segment lines for All segments (dynamic parent) tooltip */
  const segmentsSelectionTooltip = useMemo(() => {
    if (!isDynamicParent || !dynamicFilterConfig?.childFilters) return "";
    const childrenByParent = new Map<string, any>(
      Object.values(dynamicFilterConfig.childFilters as Record<string, any>)
        .filter((c: any) => c?.parent)
        .map((c: any) => [c.parent as string, c])
    );
    const order = Object.values(dynamicFilterConfig.childFilters as Record<string, any>)
      .map((c: any) => c.parent as string)
      .filter(Boolean);
    const lines: string[] = [];
    for (const parent of order) {
      const row = mergedSegmentChildByParent.get(parent);
      if (!row?.selected?.length) continue;
      const child = childrenByParent.get(parent);
      if (!child) continue;
      const fromChild =
        buildSelectedFilterMapFromOptions(row.selected.map(String), child.options) ?? {};
      const resolved = row.selected.map(
        (s, i) =>
          fromChild[String(s)] ??
          row.selectedFilterMap?.[String(s)] ??
          row.selectedLabels?.[i] ??
          s
      );
      lines.push(`${child.label}: ${resolved.join(", ")}`);
    }
    return lines.length > 0 ? lines.join("\n") : "";
  }, [isDynamicParent, dynamicFilterConfig, mergedSegmentChildByParent]);

  const filterTooltipLabel = useMemo(() => {
    if (isNewChat) return "";
    if (state.count >= 1) {
      if (isDynamicParent) {
        const labelSummary = Object.values(selectedFilterMap).join(", ");
        const raw =
          segmentsSelectionTooltip ||
          (labelSummary ? `Selected (${state.count}): ${labelSummary}` : `Selected (${state.count}): ${value.join(", ")}`);
        return raw.replace(/\n/g, ", ");
      }
      return state.selectedLabelsComma;
    }
    // Follow-up chat with no selection: still show a tooltip (button already shows attributes.label).
    return attributes.label || "";
  }, [
    isNewChat,
    state.count,
    state.selectedLabelsComma,
    isDynamicParent,
    segmentsSelectionTooltip,
    selectedFilterMap,
    value,
    attributes.label,
  ]);

  useEffect(() => {
    if (!filterTooltipLabel) setSelectionTooltipOpened(false);
  }, [filterTooltipLabel]);

  useEffect(() => {
    if (menuInteractionDisabled) setOpened(false);
  }, [menuInteractionDisabled]);

  const triggerButton = (
    <UnstyledButton
      aria-label="Filter"
      aria-disabled={menuInteractionDisabled}
      tabIndex={menuInteractionDisabled ? -1 : 0}
      style={{
        height: 32,
        boxSizing: "border-box",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: "0 8px",
        background: "#FFFFFF",
        maxWidth: 180,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: menuInteractionDisabled ? "not-allowed" : "pointer",
        pointerEvents: menuInteractionDisabled ? "none" : "auto",
        minWidth: 0,
      }}
    >
      <Group spacing={6} noWrap mr={6} sx={{ minWidth: 0, flex: 1 }}>
        <>
          <IconComponent size={15} color="#1C1B1F" style={{ flexShrink: 0 }} />
          <Text
            size="sm"
            c="#212529"
            lh={1.2}
            fz={12}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {state.count >= 1 ? String(state.first) : attributes.label}
          </Text>
          {state.count - 1 >= 1 && (
            <Text size="sm" lh={1.2} fz={12} color="#4B4BE8" style={{ flexShrink: 0 }}>
              + {state.count - 1}
            </Text>
          )}
        </>
      </Group>
      {!(filterType === "productArea" && menuInteractionDisabled) && (
        <IconChevronDown size={14} color="#212529" style={{ flexShrink: 0 }} />
      )}
    </UnstyledButton>
  );

  const menuTarget =
    filterTooltipLabel !== "" ? (
      <Tooltip
        label={filterTooltipLabel}
        opened={selectionTooltipOpened}
        events={{ hover: false, focus: false, touch: false }}
        maw={360}
        withinPortal
        multiline
        withArrow
        zIndex={1000}
      >
        <Box
          component="span"
          onMouseEnter={() => setSelectionTooltipOpened(true)}
          onMouseLeave={() => setSelectionTooltipOpened(false)}
          sx={{
            display: "inline-flex",
            maxWidth: "100%",
            verticalAlign: "middle",
          }}
        >
          <Menu.Target>{triggerButton}</Menu.Target>
        </Box>
      </Tooltip>
    ) : (
      <Menu.Target>{triggerButton}</Menu.Target>
    );

  const resetFilters = (MenuType: "idea" | "evidence", selectedParentFilter?: string | null) => {
    const targetParentKey = selectedParentFilter ?? attributes.path;

    updateSessionFilter(targetParentKey, []);

    setCheckedComponents((current: any) => {
      const next: { [key: string]: any[] } = {};
      Object.keys(current).forEach((key) => {
        next[key] = key.split(".")[0] === targetParentKey ? [] : current[key];
      });
      return next;
    });

    if (targetParentKey === attributes.path) {
      setContextActiveFilters((prev: { parent?: string }[]) =>
        (prev ?? []).filter((f) => f.parent !== attributes.path)
      );
    }
  };

  const filteredInitialCustomersFilters = React.useMemo(() => {
    const { account } = ideasFiltersConfig.evidence || {};
    const accountChildFilters = _.get(account, "childFilters") || {};
    return {
      evidence: {
        ...accountChildFilters
      }
    };
  }, [ideasFiltersConfig]);

  // Use initialfilters prop - for C360 it's wrapped in { evidence: {...} }
  const evidenceFilters = filteredInitialCustomersFilters?.evidence || filteredInitialCustomersFilters || {};
  const filteredFiltersKeys = Object.keys(evidenceFilters).filter((name) => name !== '_id');

  // Parent keys for evidence/customer sub-filters (Plan, Region, NPS Group, etc.)
  const evidenceParentKeys = useMemo(
    () => new Set(filteredFiltersKeys.map((name) => evidenceFilters[name]?.parent).filter(Boolean)),
    [filteredFiltersKeys, evidenceFilters]
  );

  const clearContextCustomerFilters = useCallback(() => {
    setContextActiveFilters((prev: { parent?: string }[]) =>
      (prev ?? []).filter((f) => !f.parent || !evidenceParentKeys.has(f.parent))
    );
  }, [setContextActiveFilters, evidenceParentKeys]);

  const clearDynamicContextFilters = useCallback(() => {
    setContextActiveFilters((prev: { parent?: string }[]) =>
      (prev ?? []).filter((f) => !f.parent || !childFilterParents.has(f.parent))
    );
  }, [setContextActiveFilters, childFilterParents]);

  const clearAllDynamicParentSelections = useCallback(() => {
    childFilterParents.forEach((p) => {
      updateSessionFilter(p, []);
    });
    setCheckedComponents((current: Record<string, string[]>) => {
      const next = { ...current };
      childFilterParents.forEach((p) => {
        next[p] = [];
      });
      return next;
    });
    clearDynamicContextFilters();
  }, [childFilterParents, updateSessionFilter, setCheckedComponents, clearDynamicContextFilters]);

  if (menuInteractionDisabled && state.count === 0) return null;

  return (
    <Menu
      shadow="md"
      radius="md"
      width={270}
      position="bottom-start"
      opened={menuInteractionDisabled ? false : opened}
      onChange={(isOpen) => {
        if (menuInteractionDisabled) return;
        setOpenedAndNotifyApply(isOpen);
        if (isOpen) {
          setSelectionTooltipOpened(false);
          // For dynamic parent filters (those with childFilters), don't pre-select any child —
          // each ChildMenu sets selectedParentFilter on its own open. Pre-selecting the parent
          // key would make all children share the same first path segment and all appear highlighted.
          setSelectedParentFilter(isDynamicParent ? null : attributes.path);
        } else {
          setSelectedParentFilter(null);
        }
      }}
    >
      {menuTarget}
      <Menu.Dropdown sx={{ padding: 0, paddingTop: 5 }}>
        <Box>
          { isDynamicFilter && isDynamicParent && (
            <>
              <Box sx={{ maxHeight: 275, overflowY: 'scroll' }}>
                {Object.values((dynamicFilterConfig?.childFilters ?? {}) as Record<string, any>).map(
                  (childFilter: any, index: number) => (
                    <Box key={childFilter.name ?? index} mt={index === 0 ? 5 : 0} w="100%">
                      <ChildMenu
                        label={childFilter.label}
                        initialfilters={dynamicInitialFilters}
                        filter={childFilter}
                        MenuType={childFilter.filterObject === 'idea' ? 'idea' : 'evidence'}
                        resetFilters={resetFilters}
                        ideasFiltersConfig={ideasFiltersConfig}
                      />
                    </Box>
                  )
                )}
              </Box>
              <FilterFooter
                onClickClear={() => {
                  clearAllDynamicParentSelections();
                }}
                editMode={editMode}
              />
            </>
          ) }
          { filterType === 'dynamic' && !isDynamicParent && (
            <CheckboxFilter
              initialfilters={ideasFiltersConfig[configRoute]}
              MenuType={configRoute}
              resetFilters={resetFilters}
              ideasFiltersConfig={ideasFiltersConfig}
              setOpened={setOpenedAndNotifyApply}
              setValue={setValueForChild}
            />
          ) }
          { filterType === 'productArea' && (
            <CheckboxFilter
              initialfilters={ideasFiltersConfig.idea}
              MenuType="idea"
              resetFilters={resetFilters}
              ideasFiltersConfig={ideasFiltersConfig}
              setOpened={setOpenedAndNotifyApply}
              setValue={setValueForChild}
            />
          ) }
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};

export default ExploreFilter;
